/**
 * Atlas Search — filter by date range + text across live tasks + trash.
 *
 * Fully client-side (uses the same /atlas/board + /atlas/trash endpoints).
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MagnifyingGlass } from "@phosphor-icons/react";

function localKey(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

const URG_STYLE = {
  none: "bg-slate-100 text-slate-600",
  green: "bg-emerald-100 text-emerald-700",
  yellow: "bg-amber-100 text-amber-700",
  red: "bg-rose-100 text-rose-700",
};

export default function AtlasSearch() {
  const [q, setQ] = useState("");
  const [date, setDate] = useState("");
  const [board, setBoard] = useState(null);
  const [trash, setTrash] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeTrash, setIncludeTrash] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [b, t] = await Promise.all([
          api.get("/atlas/board"),
          api.get("/atlas/trash"),
        ]);
        setBoard(b.data);
        setTrash(t.data || []);
      } catch (e) {
        const m = apiErrorMessage(e); if (m) toast.error(m);
      } finally { setLoading(false); }
    })();
  }, []);

  const results = useMemo(() => {
    if (!board) return [];
    const colByKey = Object.fromEntries((board.columns || []).map((c) => [c.key, c]));
    const live = (board.tasks || []).map((t) => ({
      ...t, _origin: "live", _column: colByKey[t.column_key],
    }));
    const trashed = includeTrash ? trash.map((t) => ({ ...t, _origin: "trash", _column: colByKey[t.column_key] })) : [];
    const all = [...live, ...trashed];
    const needle = q.trim().toLowerCase();
    return all.filter((t) => {
      if (needle && !(t.text || "").toLowerCase().includes(needle)) return false;
      if (date) {
        const created = localKey(t.created_at);
        const deleted = localKey(t.deleted_at);
        if (created !== date && deleted !== date) return false;
      }
      return true;
    }).sort((a, b) => {
      const at = new Date(a.deleted_at || a.updated_at || a.created_at || 0).getTime();
      const bt = new Date(b.deleted_at || b.updated_at || b.created_at || 0).getTime();
      return bt - at;
    });
  }, [board, trash, q, date, includeTrash]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="atlas-search">
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Search tasks</Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type any word from a task…"
              className="pl-8"
              data-testid="atlas-search-input"
            />
          </div>
        </div>
        <div className="w-48">
          <Label className="text-xs">Date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="atlas-search-date" />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input type="checkbox" checked={includeTrash} onChange={(e) => setIncludeTrash(e.target.checked)} data-testid="atlas-search-trash-toggle" />
          Include trash
        </label>
        <div className="text-xs text-slate-500 ml-auto" data-testid="atlas-search-count">
          {loading ? "Loading…" : `${results.length} result${results.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {!loading && results.length === 0 && (
        <div className="text-sm text-slate-500 italic py-8 text-center">
          No tasks match your filters.
        </div>
      )}
      <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
        {results.map((t) => (
          <div
            key={`${t._origin}:${t.id}`}
            className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[13px]"
            data-testid={`atlas-search-row-${t.id}`}
          >
            <span className={`inline-flex px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded ${URG_STYLE[t.urgency || "none"]}`}>
              {t.urgency || "none"}
            </span>
            <span className={`flex-1 min-w-0 break-words ${(t.urgency || "none") === "none" ? "text-slate-500" : "text-slate-800"}`}>
              {t.text}
            </span>
            <span className="text-[10px] text-slate-500 whitespace-nowrap">
              {(t._column?.group_name ? t._column.group_name + " · " : "") + (t._column?.name || t.column_key)}
            </span>
            {t._origin === "trash" ? (
              <span className="text-[10px] font-semibold text-rose-500 uppercase">trash</span>
            ) : (
              <span className="text-[10px] font-semibold text-emerald-500 uppercase">live</span>
            )}
            <span className="text-[10px] text-slate-400 whitespace-nowrap">
              {(t.deleted_at ? "del " : "add ") + localKey(t.deleted_at || t.created_at)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
